// ===== World 5: Lazy cats – CHAIN REACTION (energy that is passed on) =====
// Books topple like dominoes, a toy mouse swings on a string, and lazy cats only wake up when something hits them.
// The ball is a ball of yarn with a face; the goal is the cat basket.
import { defineWorld, definePart, defineObj } from '../../core/registry';
import type { Act, Ctx2D, Gfx, GameBody, IntroFn, IntroHelpers, Level, ObjData, PartDef, Placement, Reskin, SkinDef, SoundFn, Static, Vec, WorldMeta } from '../../core/types';
import type Matter from 'matter-js';
import { selBox } from '../../core/draw-helpers';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG, BOOK, type BookType } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const GX = 1, KICK = 0.05;            // extra gravity while a book topples / spin kick when it is hit
const PEND = 130, MOUSE = 21;         // string length and mouse radius
let CUR: Level | null = null;         // the level built most recently (set in setup)
const BOOKCOLS: [string, string][] = [['#FF8FA3', '#E0527D'], ['#7FD3FF', '#3E9FD8'], ['#9BE15D', '#3FA535'], ['#FFE07A', '#F2A516'], ['#C9B6F2', '#9A83DB'], ['#FFB86B', '#E07B2E']];
const CATC: Record<string, [string, string]> = { orange: ['#FFB27A', '#F08A3E'], gray: ['#B3A6B8', '#7D6E84'], white: ['#FFFFFF', '#DCD6E6'], black: ['#6A6178', '#3E3650'] };

/** Lazy cat: { x, y (centre of the body), dir (1 = head to the right), col, mode: 'swat' | 'jump', power, up, to: {x,y}, act } */
interface CatData extends ObjData { dir?: number; col?: string; mode?: 'swat' | 'jump'; power?: number; up?: number; to?: Vec; act?: Act }
interface CatState { body: GameBody; head: GameBody; awake: number; dir: number; done: boolean; paw: number; jumpT: number }
/** What catPic needs from the cat's state */
type CatPose = { awake: number | boolean; paw?: number };

// ---------- drawing helpers: books (dominoes) ----------
function surfTop(s: Static, x: number) {
  const a = (s.a || 0) * DEG; if (Math.abs(a) > 3 * DEG || s.sensor) return null;
  const c = Math.cos(a), lx = (x - s.x) / c;
  if (Math.abs(lx) > s.w / 2 - 4) return null;
  return s.y + lx * Math.sin(a) - (s.h / 2) / c;
}
// the book "lands" on the nearest level shelf below it (so it always stands neatly)
function landY(level: Level | null, pl: Placement, h: number) {
  if (!level) return pl.y;
  let best: number | null = null;
  for (const s of level.statics || []) {
    const t = surfTop(s, pl.x); if (t == null) continue;
    if (t >= pl.y - 4 && t <= pl.y + h / 2 + 170 && (best == null || t < best)) best = t;
  }
  return best == null ? pl.y : Math.round(best - h / 2 - 1);
}
function colorOf(pl: Vec) { return BOOKCOLS[Math.abs(Math.round(pl.x / 9)) % BOOKCOLS.length]; }

function drawBook(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, col: [string, string], sel: boolean, g: Gfx, small?: boolean) {
  g.block(ctx, x, y, w, h, a, col[0], col[1], 4, { outline: sel ? '#fff' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.fillStyle = 'rgba(255,240,190,.9)';
  const bw = w - 6;
  g.rr(ctx, -bw / 2, -h / 2 + 8, bw, 4, 2); ctx.fill();
  g.rr(ctx, -bw / 2, h / 2 - 12, bw, 4, 2); ctx.fill();
  if (small) { ctx.beginPath(); ctx.arc(0, 0, 5, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke(); }
  else { g.rr(ctx, -bw / 2 + 2, -12, bw - 4, 24, 3); ctx.fillStyle = '#FFF8E6'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.45)'; ctx.stroke();
    ctx.fillStyle = col[1]; ctx.beginPath(); ctx.arc(0, 0, 4, 0, 7); ctx.fill(); }
  ctx.restore();
}

// ---------- drawing helpers: ball of yarn (the ball) ----------
function yarn(ctx: Ctx2D, x: number, y: number, r: number, roll: number | undefined, c0: string, c1: string, c2: string, g: Gfx) {
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .15, 0, 0, r * 1.05);
  gr.addColorStop(0, c0); gr.addColorStop(0.55, c1); gr.addColorStop(1, c2);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.save(); ctx.beginPath(); ctx.arc(0, 0, r - 1, 0, 7); ctx.clip(); ctx.rotate(roll || 0);
  ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = Math.max(1.6, r * 0.11); ctx.lineCap = 'round';
  for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.ellipse(i * r * 0.28, 0, r * 0.35, r * 1.05, 0.5, 0, 7); ctx.stroke(); }
  ctx.strokeStyle = 'rgba(0,0,0,.12)';
  for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.ellipse(0, i * r * 0.4, r * 1.1, r * 0.3, -0.35, 0, 7); ctx.stroke(); }
  ctx.restore();
  ctx.save(); ctx.rotate(roll || 0);
  ctx.strokeStyle = c2; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(r * 0.7, r * 0.7); ctx.quadraticCurveTo(r * 1.25, r * 0.9, r * 1.1, r * 1.35); ctx.stroke();
  ctx.restore();
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.55)'; ctx.stroke();
  ctx.restore();
}
// Local face, not draw-helpers' face: the eyes have an outline and a highlight, the face does not roll,
// and the sizes differ slightly (eyes further apart, pinker cheeks).
function face(ctx: Ctx2D, x: number, y: number, r: number, v: Vec, g: Gfx, sad?: boolean) {
  const sp = Math.hypot(v.x, v.y), lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
  const scared = v.y > 9, fast = sp > 8.5;
  const ex = r * 0.36, ey = -r * 0.1, er = r * 0.27;
  ctx.save(); ctx.translate(x, y);
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.58), 0, 7); ctx.fillStyle = INK; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.6 + 1.2, ey + ly * 0.6 - 1.2, er * 0.18, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (sad) { ctx.beginPath(); ctx.arc(0, r * 0.62, r * 0.24, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.42, r * 0.16, r * 0.2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.26, r * 0.26, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,90,130,.45)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.62, r * 0.24, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
  ctx.restore();
}

// ---------- drawing helpers: toy mouse on a string (pendulum) ----------
function mouse(ctx: Ctx2D, x: number, y: number, a: number, dir: number, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(dir, 1);
  ctx.strokeStyle = '#E58AB8'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(-MOUSE + 2, 4); ctx.bezierCurveTo(-MOUSE - 14, 0, -MOUSE - 10, 18, -MOUSE - 22, 14); ctx.stroke();
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.ellipse(0, 0, MOUSE + 3, MOUSE - 1, 0, 0, 7);
  ctx.fillStyle = g.lg(ctx, -MOUSE, MOUSE, '#D9D3E6', '#9C94B3'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  for (const ex of [-4, 8]) { ctx.beginPath(); ctx.arc(ex, -MOUSE + 2, 8, 0, 7); ctx.fillStyle = '#C9C3D6'; ctx.fill(); ctx.stroke(); ctx.beginPath(); ctx.arc(ex, -MOUSE + 2, 4.5, 0, 7); ctx.fillStyle = '#FFB3C7'; ctx.fill(); }
  ctx.beginPath(); ctx.arc(12, -3, 3.2, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.beginPath(); ctx.arc(MOUSE + 2, 2, 4, 0, 7); ctx.fillStyle = '#FF7AA2'; ctx.fill();
  ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(MOUSE - 2, 5); ctx.lineTo(MOUSE + 10, 9); ctx.moveTo(MOUSE - 2, 3); ctx.lineTo(MOUSE + 11, 2); ctx.stroke();
  ctx.restore();
}
function peg(ctx: Ctx2D, x: number, y: number, g: Gfx) {
  ctx.save();
  g.shadowOn(ctx, 6, 3); g.rr(ctx, x - 22, y - 12, 44, 12, 5); ctx.fillStyle = g.lg(ctx, y - 12, y, '#D9A27A', '#A8703F'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(x, y + 1, 4.5, 0, 7); ctx.fillStyle = '#FFE07A'; ctx.fill(); ctx.stroke();
  ctx.restore();
}
function string(ctx: Ctx2D, x0: number, y0: number, x1: number, y1: number) {
  ctx.save(); ctx.lineCap = 'round';
  ctx.strokeStyle = '#E0527D'; ctx.lineWidth = 3.5; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 5]); ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
  ctx.restore();
}
function drawPendulumBuild(ctx: Ctx2D, pl: Placement, sel: boolean, g: Gfx) {
  const dir = pl.a ? -1 : 1;
  ctx.save();
  ctx.strokeStyle = 'rgba(59,42,92,.3)'; ctx.lineWidth = 3; ctx.setLineDash([5, 9]); ctx.lineCap = 'round';
  ctx.beginPath(); ctx.arc(pl.x, pl.y, PEND, dir > 0 ? Math.PI * 0.95 : Math.PI * 0.05, dir > 0 ? Math.PI * 0.2 : Math.PI * 0.8, dir > 0); ctx.stroke();
  ctx.setLineDash([]);
  const ea = dir > 0 ? Math.PI * 0.2 : Math.PI * 0.8, ex = pl.x + Math.cos(ea) * PEND, ey = pl.y + Math.sin(ea) * PEND;
  ctx.fillStyle = 'rgba(59,42,92,.35)'; ctx.translate(ex, ey); ctx.rotate(ea + (dir > 0 ? -Math.PI / 2 : Math.PI / 2));
  ctx.beginPath(); ctx.moveTo(10, 0); ctx.lineTo(-6, -8); ctx.lineTo(-6, 8); ctx.closePath(); ctx.fill();
  ctx.restore();
  string(ctx, pl.x, pl.y, pl.x - dir * PEND, pl.y);
  peg(ctx, pl.x, pl.y, g);
  mouse(ctx, pl.x - dir * PEND, pl.y, 0, dir, g);
  if (sel) selBox(ctx, Math.min(pl.x, pl.x - dir * PEND) - 30, pl.y - 30, PEND + 60, 60, 20);
}

// ---------- drawing helpers: lazy cats ----------
function zz(ctx: Ctx2D, x: number, y: number, s: number) {
  ctx.save(); ctx.translate(x, y); ctx.scale(s, s);
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(-6, -6); ctx.lineTo(6, -6); ctx.lineTo(-6, 6); ctx.lineTo(6, 6);
  ctx.strokeStyle = INK; ctx.lineWidth = 6; ctx.stroke(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.stroke();
  ctx.restore();
}
function catPic(ctx: Ctx2D, x: number, y: number, dir: number, col: string | undefined, st: CatPose, t: number, g: Gfx) {
  const c = (col && CATC[col]) || CATC.orange;
  const awake = !!st.awake, br = awake ? 0 : Math.sin(t * 0.06) * 1.5;
  ctx.save(); ctx.translate(x, y); ctx.scale(dir, 1);
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  const tw = awake ? Math.sin(t * 0.3) * 10 : Math.sin(t * 0.04) * 4;
  ctx.lineWidth = 12; ctx.strokeStyle = INK;
  ctx.beginPath(); ctx.moveTo(-52, 8); ctx.quadraticCurveTo(-86, 10, -78 + tw * 0.3, -18 + tw); ctx.stroke();
  ctx.lineWidth = 7; ctx.strokeStyle = c[0]; ctx.stroke();
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.ellipse(-4, 2 - br * 0.5, 60, 23 + br, 0, 0, 7); ctx.fillStyle = g.lg(ctx, -22, 24, c[0], c[1]); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  if (col === 'orange' || !col) { ctx.strokeStyle = 'rgba(200,100,30,.5)'; ctx.lineWidth = 4; for (const sx of [-30, -14, 2]) { ctx.beginPath(); ctx.moveTo(sx, -18); ctx.quadraticCurveTo(sx + 4, -8, sx, 0); ctx.stroke(); } }
  const pw = st.paw || 0;
  if (pw > 0) {
    ctx.save(); ctx.translate(40, 8); ctx.rotate(-0.25 * pw);
    g.rr(ctx, 0, -8, 30 + 34 * pw, 16, 8); ctx.fillStyle = c[0]; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.beginPath(); ctx.arc(30 + 34 * pw - 4, 0, 4, 0, 7); ctx.fillStyle = '#FF9FB5'; ctx.fill();
    ctx.restore();
  } else {
    g.rr(ctx, 26, 12, 34, 13, 6); ctx.fillStyle = c[0]; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  }
  const hx = 48, hy = -14 + (awake ? -6 : 0);
  ctx.fillStyle = c[0]; ctx.strokeStyle = INK; ctx.lineWidth = 3;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.moveTo(hx + s * 20, hy - 10); ctx.lineTo(hx + s * 17, hy - 34 - (awake ? 4 : 0)); ctx.lineTo(hx + s * 4, hy - 20); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(hx + s * 16, hy - 14); ctx.lineTo(hx + s * 15, hy - 27); ctx.lineTo(hx + s * 8, hy - 19); ctx.closePath(); ctx.fillStyle = '#FFB3C7'; ctx.fill(); ctx.fillStyle = c[0];
  }
  ctx.beginPath(); ctx.arc(hx, hy, 24, 0, 7); ctx.fillStyle = g.lg(ctx, hy - 24, hy + 24, c[0], c[1]); ctx.fill(); ctx.stroke();
  ctx.strokeStyle = INK; ctx.lineWidth = 2.5;
  if (awake) {
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(hx + s * 9, hy - 3, 5.5, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.stroke(); ctx.beginPath(); ctx.arc(hx + s * 9 + 1.5, hy - 3, 3, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
    ctx.beginPath(); ctx.ellipse(hx + 1, hy + 11, 4, 5, 0, 0, 7); ctx.fillStyle = INK; ctx.fill();
  } else {
    for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(hx + s * 9, hy - 4, 5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
    ctx.beginPath(); ctx.arc(hx + 1, hy + 6, 4, 0.1 * Math.PI, 0.9 * Math.PI); ctx.stroke();
  }
  ctx.beginPath(); ctx.arc(hx + 1, hy + 4, 3, 0, 7); ctx.fillStyle = '#FF8FA3'; ctx.fill();
  ctx.strokeStyle = 'rgba(59,42,92,.7)'; ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.moveTo(hx + 14, hy + 4); ctx.lineTo(hx + 30, hy + 1); ctx.moveTo(hx + 14, hy + 8); ctx.lineTo(hx + 29, hy + 10);
  ctx.moveTo(hx - 12, hy + 4); ctx.lineTo(hx - 27, hy + 1); ctx.moveTo(hx - 12, hy + 8); ctx.lineTo(hx - 26, hy + 10); ctx.stroke();
  ctx.restore();
  if (!awake) { for (const off of [0, 75]) { const k = ((t + off) % 150) / 150; zz(ctx, x + dir * (40 + k * 22), y - 56 - k * 36, 0.7 + k * 0.5); } }
}

// ---------- drawing helpers: intro animations (460 x 280) ----------
function introBooks(ctx: Ctx2D, t: number, g: Gfx, h: IntroHelpers) {
  const fy = 232;
  g.block(ctx, 250, fy + 12, 400, 22, 0, '#D9A27A', '#B07A52', 8);
  const bx = [150, 210, 270, 330], t0 = 50, dt = 16;
  for (let i = 0; i < bx.length; i++) {
    const k = h.ease((t - t0 - i * dt) / 22);
    const ang = Math.min(i === bx.length - 1 ? 1.57 : 1.1, k * 1.6);
    ctx.save(); ctx.translate(bx[i] + 13, fy); ctx.rotate(ang); ctx.translate(-13, -46);
    drawBook(ctx, 0, 0, 0, 26, 92, BOOKCOLS[i], false, g); ctx.restore();
  }
  const x = t < t0 ? 40 + (bx[0] - 28 - 40) * t / t0 : bx[0] - 28 - Math.min(20, (t - t0) * 0.6);
  yarn(ctx, x, fy - 15, 15, x / 15, '#D6F1FF', '#7FD3FF', '#3E9FD8', g);
}
const introCat: IntroFn = (ctx, t, g) => {
  const fy = 232;
  g.block(ctx, 250, fy + 12, 420, 22, 0, '#D9A27A', '#B07A52', 8);
  const hitT = 70, awake = t >= hitT;
  const bx = Math.min(115, 30 + t * 1.25);
  const k = t - hitT;
  const paw = !awake ? 0 : k < 12 ? 0 : k < 18 ? (k - 12) / 6 : k < 40 ? 1 : Math.max(0, 1 - (k - 40) / 10);
  catPic(ctx, 200, fy - 22, 1, 'orange', { awake, paw }, t, g);
  const b2 = 290 + (k > 16 ? Math.min(160, (k - 16) * 7) : 0);
  yarn(ctx, b2, fy - 15, 15, b2 / 15, '#FFE0EA', '#FF8FB1', '#D94C7A', g);
  face(ctx, b2, fy - 15, 15, { x: k > 16 ? 6 : 0, y: 0 }, g);
  yarn(ctx, awake ? 115 - Math.min(20, k * 0.8) : bx, fy - 15, 15, bx / 15, '#D6F1FF', '#7FD3FF', '#3E9FD8', g);
};
const introPendulum: IntroFn = (ctx, t, g) => {
  const fy = 232, ax = 200, ay = 80, Lr = fy - 15 - ay;
  g.block(ctx, 280, fy + 12, 380, 22, 0, '#D9A27A', '#B07A52', 8);
  const k = Math.min(1, t / 34);
  const a2 = t <= 34 ? Math.PI - (Math.PI / 2) * k * k : Math.PI / 2 - Math.sin((t - 34) * 0.06) * 0.3;
  const mx = ax + Math.cos(a2) * Lr, my = ay + Math.sin(a2) * Lr;
  string(ctx, ax, ay, mx, my); peg(ctx, ax, ay, g);
  const bx = 236 + (t > 34 ? Math.min(200, (t - 34) * 6) : 0);
  yarn(ctx, bx, fy - 15, 15, bx / 15, '#FFE0EA', '#FF8FB1', '#D94C7A', g);
  face(ctx, bx, fy - 15, 15, { x: t > 34 ? 6 : 0, y: 0 }, g);
  mouse(ctx, mx, my, a2 - Math.PI / 2, 1, g);
};

// ---------- drawing helpers: cat basket (the goal) ----------
function heart(ctx: Ctx2D, x: number, y: number, s: number, a: number) {
  ctx.save(); ctx.globalAlpha = Math.max(0, a); ctx.translate(x, y); ctx.scale(s / 6, s / 6);
  ctx.beginPath(); ctx.moveTo(0, 5); ctx.bezierCurveTo(-8, -1, -5, -8, 0, -3); ctx.bezierCurveTo(5, -8, 8, -1, 0, 5); ctx.fillStyle = '#FF5A8A'; ctx.fill();
  ctx.restore();
}
// Local basket, not draw-helpers' basket: rounded bottom, curved weave, a kitten peeking out and hearts when full.
function basket(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) {
  const { x, y, w } = gl;
  ctx.save();
  const kx = x + w / 2 - 34, ky = y - 66;
  ctx.save(); ctx.translate(kx, ky);
  ctx.fillStyle = '#FFB27A'; ctx.strokeStyle = INK; ctx.lineWidth = 3;
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.moveTo(s * 14, -8); ctx.lineTo(s * 13, -26); ctx.lineTo(s * 3, -14); ctx.closePath(); ctx.fill(); ctx.stroke(); }
  ctx.beginPath(); ctx.arc(0, 0, 18, 0, 7); ctx.fill(); ctx.stroke();
  ctx.lineWidth = 2.2;
  if (full) { for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * 7, -3, 3.4, 0, 7); ctx.fillStyle = INK; ctx.fill(); } ctx.beginPath(); ctx.arc(0, 5, 5, 0.1 * Math.PI, 0.9 * Math.PI); ctx.stroke(); }
  else { for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * 7, -3, 4, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); } }
  ctx.beginPath(); ctx.arc(0, 3, 2.4, 0, 7); ctx.fillStyle = '#FF8FA3'; ctx.fill();
  ctx.restore();
  g.shadowOn(ctx, 14, 7);
  ctx.beginPath();
  ctx.moveTo(x - w / 2 - 8, y - 58); ctx.quadraticCurveTo(x - w / 2, y + 8, x, y + 8); ctx.quadraticCurveTo(x + w / 2, y + 8, x + w / 2 + 8, y - 58);
  ctx.closePath();
  ctx.fillStyle = g.lg(ctx, y - 60, y + 8, '#F2C27E', '#C98A4A'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.save(); ctx.clip();
  ctx.strokeStyle = 'rgba(140,80,30,.45)'; ctx.lineWidth = 3;
  for (let yy = y - 44; yy < y + 8; yy += 14) { ctx.beginPath(); ctx.moveTo(x - w / 2 - 10, yy); ctx.quadraticCurveTo(x, yy + 8, x + w / 2 + 10, yy); ctx.stroke(); }
  for (let xx = x - w / 2 + 6; xx < x + w / 2; xx += 18) { ctx.beginPath(); ctx.moveTo(xx, y - 60); ctx.lineTo(xx + (xx - x) * 0.12, y + 10); ctx.stroke(); }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 8, 10, 0, 0, 7); ctx.fillStyle = '#C9B6F2'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(x, y - 57, w / 2 - 4, 6, 0, 0, 7); ctx.fillStyle = '#E6DBFF'; ctx.fill();
  if (full) for (let i = 0; i < 3; i++) { const k = ((t + i * 40) % 120) / 120; heart(ctx, x - 30 + i * 30, y - 80 - k * 40, 6 * (1 - k * 0.4), 1 - k); }
  ctx.restore();
}

// ---------- drawing helpers: kit parts in cat style ----------
function scratcher(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {   // plank = cardboard scratching board
  g.block(ctx, x, y, w, h, a, '#F2D3A0', '#C9A06A', 6, { outline: sel ? '#fff' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.strokeStyle = 'rgba(140,90,40,.5)'; ctx.lineWidth = 2;
  for (let xx = -w / 2 + 10; xx < w / 2 - 6; xx += 8) { ctx.beginPath(); ctx.moveTo(xx, -h / 2 + 3); ctx.lineTo(xx + 3, h / 2 - 2); ctx.stroke(); }
  ctx.fillStyle = '#FF8FA3'; g.rr(ctx, -12, -h / 2 - 1, 24, 6, 3); ctx.fill();
  ctx.restore();
}
function cushion(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {      // pad = soft cat cushion on springs
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.strokeStyle = '#8B84A0'; ctx.lineWidth = 3;
  for (const px of [-w / 3, 0, w / 3]) { ctx.beginPath(); ctx.moveTo(px, h / 2 + 10); for (let i = 0; i < 4; i++) ctx.lineTo(px + (i % 2 ? 6 : -6), h / 2 + 8 - i * 3); ctx.stroke(); }
  g.rr(ctx, -w / 2 - 4, h / 2 + 6, w + 8, 8, 4); ctx.fillStyle = '#B07A52'; ctx.fill();
  g.shadowOn(ctx, 8, 4);
  g.rr(ctx, -w / 2, -h / 2 - 6, w, h + 8, (h + 8) / 2); ctx.fillStyle = g.lg(ctx, -h / 2 - 6, h / 2 + 2, '#FFB3C7', '#E0527D'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.7)';
  for (const px of [-w / 4, 0, w / 4]) { ctx.beginPath(); ctx.arc(px, -2, 3, 0, 7); ctx.fill(); }
  if (sel) selBox(ctx, -w / 2 - 6, -h / 2 - 12, w + 12, h + 34, 12);
  ctx.restore();
}
function tin(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean, g: Gfx) {                                 // stone = heavy tin of cat food
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 6);
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7); ctx.fillStyle = g.lg(ctx, -24, 24, '#E9E6F2', '#9E97B5'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 18, 0, 7); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.45)'; ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-9, 0); ctx.quadraticCurveTo(2, -9, 11, 0); ctx.quadraticCurveTo(2, 9, -9, 0); ctx.moveTo(-9, 0); ctx.lineTo(-15, -5); ctx.lineTo(-15, 5); ctx.closePath();
  ctx.fillStyle = '#7FD3FF'; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
  if (sel) { ctx.save(); ctx.beginPath(); ctx.arc(x, y, 30, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); ctx.restore(); }
}

// ---------- skins (static blocks) ----------
const skins: Record<string, SkinDef> = {
  cats_shelf(ctx, b, g) { g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, '#E3AE82', '#B07A52', 7); },
  cats_sofa(ctx, b, g) {
    const { x, y } = b.position, dw = b.dw!, dh = b.dh!;
    g.block(ctx, x, y, dw, dh, b.angle, '#F59C95', '#D95F58', Math.min(16, dh / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(255,255,255,.5)';
    for (let xx = -dw / 2 + 30; xx < dw / 2 - 20; xx += 60) { ctx.beginPath(); ctx.arc(xx, 0, 3, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  cats_post(ctx, b, g) {                                   // scratching-post pole (sisal)
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#F2DDB0', '#CDAA6E', Math.min(8, w / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2 + 2, -h / 2 + 2, w - 4, h - 4, 5); ctx.clip();
    ctx.strokeStyle = 'rgba(150,100,40,.45)'; ctx.lineWidth = 2.5;
    for (let yy = -h / 2 + 6; yy < h / 2 + w; yy += 7) { ctx.beginPath(); ctx.moveTo(-w / 2, yy); ctx.lineTo(w / 2, yy - w * 0.35); ctx.stroke(); }
    ctx.restore();
  },
};

// ---------- parts ----------
function bookPart(t: BookType): PartDef {
  const B = BOOK[t], small = t === 'cats_booklet';
  return definePart({
    rects: [{ x: 0, y: 0, w: B.w, h: B.h }], rot: false,
    draw(ctx, pl, sel, g) {
      const y = landY(CUR, pl, B.h); pl.y = y;
      drawBook(ctx, pl.x, y, 0, B.w, B.h, colorOf(pl), sel, g, small);
    },
    icon(ctx, g) {
      if (small) { drawBook(ctx, -12, 12, 0, B.w, B.h, BOOKCOLS[3], false, g, true); drawBook(ctx, 14, 12, 0, B.w, B.h, BOOKCOLS[2], false, g, true); }
      else { drawBook(ctx, -20, 0, 0, B.w, B.h, BOOKCOLS[0], false, g); drawBook(ctx, 16, 4, 0.32, B.w, B.h, BOOKCOLS[1], false, g); }
    },
    intro: small ? undefined : introBooks,
    create(api, pl) {
      const y = landY(api.level, pl, B.h);
      const b = api.add(api.R(pl.x, y, B.w, B.h, { density: B.d, friction: 0.05, frictionStatic: 0.1, restitution: 0.02, frictionAir: 0.004, chamfer: { radius: 3 } }));
      b.kind = t; api.dynamic(b);
      const col = colorOf(pl);
      let lastT = -99, pin: Matter.Constraint | null = null, pinOff = false;
      // the book topples over its bottom edge (like a domino) – it doesn't just slide along the shelf
      const hinge = (s: number) => {
        if (pin) return;
        const c = Math.cos(b.angle), sn = Math.sin(b.angle), lx = s * B.w / 2, ly = B.h / 2;
        const off = { x: lx * c - ly * sn, y: lx * sn + ly * c };
        pin = api.Constraint.create({ pointA: { x: b.position.x + off.x, y: b.position.y + off.y }, bodyB: b, pointB: off, length: 0, stiffness: 0.9 });
        api.Composite.add(api.world, pin);
      };
      api.onHit(b, o => {
        if (!pin && api.S.t > 2 && (o.speed > 0.6 || Math.abs(o.angularVelocity) > 0.02)) {
          const s = o.position.x < b.position.x ? 1 : -1; hinge(s);
          if (b.angularVelocity * s < KICK) api.Body.setAngularVelocity(b, s * KICK);     // a push makes the book topple
        }
        if (Math.abs(b.angularVelocity) > 0.03 && api.S.t - lastT > 10) { lastT = api.S.t; api.sfx('cats_bonk', b.position.x, b.position.y); }
      });
      return {
        layer: 'mid',
        step() {
          if (!pin && Math.abs(b.angle) > 0.04) hinge(Math.sign(b.angle));
          // a little extra gravity while the book topples, so the chain keeps going (the books are big)
          if (pin && !pinOff && Math.abs(b.angle) < 1.45) api.Body.applyForce(b, b.position, { x: 0, y: b.mass * 0.001 * GX });
          // nearly over: release the hinge so the book can slide/tip over an edge
          if (pin && !pinOff && Math.abs(b.angle) > 1.3) { pinOff = true; b.friction = 0.6; api.Composite.remove(api.world, pin); }
        },
        draw(ctx, tt, g) { drawBook(ctx, b.position.x, b.position.y, b.angle, B.w, B.h, col, false, g, small); },
      };
    },
  });
}

const parts = {
  cats_book: bookPart('cats_book'),
  cats_booklet: bookPart('cats_booklet'),
  cats_pendulum: definePart({
    rects: [{ x: 0, y: 0, w: 50, h: 20 }, { x: -PEND, y: 0, w: 48, h: 44 }], rot: 'flip', defaultA: 0,
    draw(ctx, pl, sel, g) { drawPendulumBuild(ctx, pl, sel, g); },
    icon(ctx, g) { string(ctx, 22, -34, -22, 0); peg(ctx, 22, -34, g); mouse(ctx, -22, 8, 0.3, 1, g); },
    intro: introPendulum,
    create(api, pl) {
      const dir = pl.a ? -1 : 1;
      const b = api.add(api.Bodies.circle(pl.x - dir * PEND, pl.y, MOUSE, { density: 0.004, friction: 0.1, restitution: 0.3, frictionAir: 0.0005 }));
      b.kind = 'cats_mouse'; api.dynamic(b);
      const c = api.Constraint.create({ pointA: { x: pl.x, y: pl.y }, bodyB: b, pointB: { x: 0, y: 0 }, length: PEND, stiffness: 1 });
      api.Composite.add(api.world, c);
      let lastT = -99;
      api.onHit(b, () => { if (b.speed > 2 && api.S.t - lastT > 20) { lastT = api.S.t; api.sfx('cats_swat', b.position.x, b.position.y); } });
      return {
        layer: 'mid',
        draw(ctx, t, g) {
          const dx = b.position.x - pl.x, dy = b.position.y - pl.y;
          string(ctx, pl.x, pl.y, b.position.x, b.position.y); peg(ctx, pl.x, pl.y, g);
          mouse(ctx, b.position.x, b.position.y, Math.atan2(dy, dx) - Math.PI / 2, 1, g);
        },
      };
    },
  }),
};

// ---------- objects ----------
const objs = {
  // Lazy cat (see CatData): wakes when something moving hits it, then swats what lies in front of its paw, or jumps away
  cats_cat: defineObj<CatState>({
    layer: 'mid',
    intro: introCat,
    blockers(o) { const d = (o as CatData).dir || 1; return [{ x: o.x - d * 20, y: o.y, r: 44 }, { x: o.x + d * 48, y: o.y - 18, r: 34 }]; },
    create(api, o) {
      const dir = (o as CatData).dir || 1;
      const body = api.add(api.R(o.x - dir * 6, o.y, 112, 44, { isStatic: true, friction: 0.4, restitution: 0.1, chamfer: { radius: 18 } }));
      const head = api.add(api.Bodies.circle(o.x + dir * 48, o.y - 16, 22, { isStatic: true, friction: 0.4, restitution: 0.1 }));
      body.kind = head.kind = 'cats_cat_body';
      const st: CatState = { body, head, awake: 0, dir, done: false, paw: 0, jumpT: 0 };
      const wake = (other: GameBody) => {
        if (st.awake || !api.isDynamic(other)) return;
        if (api.S.t < 10 || (other.speed < 1.2 && Math.abs(other.angularVelocity) < 0.02)) return;
        st.awake = api.S.t;
        api.sfx('cats_meow', o.x + dir * 40, o.y - 40, '#FFE07A', 8);
      };
      api.onHit(body, wake); api.onHit(head, wake);
      return st;
    },
    step(api, st) {
      const o = st.o as CatData;
      if (!st.awake) return;
      const k = api.S.t - st.awake;
      if (o.mode === 'jump') {
        if (!st.done && k >= 16) {
          st.done = true; st.jumpT = api.S.t;
          api.Composite.remove(api.world, st.body); api.Composite.remove(api.world, st.head);
          api.sfx('cats_hop', o.x, o.y - 30);
          if (o.act) api.act(o.act);
        }
        return;
      }
      st.paw = k < 12 ? 0 : k < 18 ? (k - 12) / 6 : k < 40 ? 1 : Math.max(0, 1 - (k - 40) / 10);
      if (!st.done && k >= 16) {
        st.done = true;
        const px = o.x + st.dir * 72;
        for (const b of api.S.dyn) {
          if (b.isStatic) continue;
          const dx = (b.position.x - px) * st.dir, dy = b.position.y - o.y;
          if (dx > -14 && dx < 70 && dy > -60 && dy < 30) api.Body.setVelocity(b, { x: st.dir * (o.power || 6), y: -(o.up || 2) });
        }
        if (o.act) api.act(o.act);
        api.sfx('cats_swat', px, o.y, '#FFFFFF', 6);
      }
    },
    busy(api, st) { return !!st.awake && api.S.t - st.awake < 50; },
    draw(ctx, st, t, g, S) {
      const o = st.o as CatData, d = o.dir || 1;
      if (o.mode === 'jump' && st.done) {
        const k = Math.min(1, ((S ? S.t : 0) - st.jumpT) / 28), to = o.to || { x: o.x, y: o.y - 200 };
        const x = o.x + (to.x - o.x) * k, y = o.y + (to.y - o.y) * k - Math.sin(k * Math.PI) * 90;
        catPic(ctx, x, y, d, o.col, { awake: 1, paw: 0 }, t, g);
        return;
      }
      catPic(ctx, o.x, o.y, d, o.col, st, t, g);
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  cats_meow: (tone) => { tone(520, 0.18, 'triangle', 0.22, 820); tone(820, 0.32, 'triangle', 0.2, 430, 0.16); tone(1040, 0.3, 'sine', 0.06, 700, 0.16); },
  cats_swat: (tone, noise) => { noise(0.08, 0.35, 2600, 1.5); tone(300, 0.1, 'sine', 0.2, 140); },
  cats_bonk: (tone, noise) => { tone(170, 0.14, 'sine', 0.28, 90); noise(0.06, 0.18, 500, 1); },
  cats_hop: (tone) => { tone(300, 0.25, 'sine', 0.22, 900); },
};

// ---------- reskins: kit parts, ball and goal in cat style ----------
const scratcherRs: Reskin = (ctx, pl, sel, g, rs) => { const r = rs[0]; scratcher(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); };
const reskin = {
  plank: scratcherRs,
  long_plank: scratcherRs,
  pad: ((ctx, pl, sel, g, rs) => { const r = rs[0]; cushion(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); }) as Reskin,
  stone: ((ctx, pl, sel, g) => { tin(ctx, pl.x, pl.y, (pl.a || 0) * DEG, sel, g); }) as Reskin,
};
const icons = {
  plank(ctx: Ctx2D, g: Gfx) { scratcher(ctx, 0, 0, -0.35, 96, 18, false, g); },
  long_plank(ctx: Ctx2D, g: Gfx) { scratcher(ctx, 0, 0, -0.35, 104, 18, false, g); },
  pad(ctx: Ctx2D, g: Gfx) { cushion(ctx, 0, 0, 0, 80, 16, false, g); },
  stone(ctx: Ctx2D, g: Gfx) { tin(ctx, 0, 0, 0, false, g); },
};
function ball(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!;
  if (kind === 'hero') { yarn(ctx, x, y, r, b.roll, '#FFE0EA', '#FF8FB1', '#D94C7A', g); face(ctx, x, y, r, b.velocity, g, b.sad); }
  else yarn(ctx, x, y, r, b.roll, '#D6F1FF', '#7FD3FF', '#3E9FD8', g);
}
const goal = {
  bowl: basket,
  front(ctx: Ctx2D, gl: { x: number; y: number; w: number }) { ctx.save(); ctx.beginPath(); ctx.ellipse(gl.x, gl.y - 58, gl.w / 2 + 8, 10, 0, 0.05, Math.PI - 0.05); ctx.lineWidth = 5; ctx.strokeStyle = '#C9B6F2'; ctx.stroke(); ctx.restore(); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🐱', strings, music: 'cats.mp3',
  credits: { title: 'Lazy Morning Coffee', artist: 'geoffharvey', id: 385886, url: 'https://pixabay.com/music/traditional-jazz-lazy-morning-coffee-385886/' },
  synth: { bpm: 62, root: 233.1, sc: 'major', prog: [1, 4, 0, 5], bar: 4, lead: 'soft', dens: 0.35, sev: true },
};

const world = defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(255,246,236,.6)',
  setup(api, level) { CUR = level; },
  skins,
  ball,
  goal,
  reskin,
  icons,
  sounds,
  parts,
  objs,
  levels,
});
export default world;
